package com.evoapps.evoshift.domain.model

/**
 * SH-267: an owner's group of workplaces (workplace_groups/{id}). Within a group the managers of
 * one workplace may plan people of another (borrowing); a workplace in no group neither lends nor
 * borrows. Each workplace is in one group at most (its [Workplace.groupId] says which). Every
 * owner starts with one [isDefault] group holding all their workplaces, which new workplaces join;
 * while it has no [name] of its own the app calls it "All workplaces" in the reader's language.
 * Saved only by the saveWorkplaceGroups function (functions/workplaceGroups.js).
 */
data class WorkplaceGroup(
    val id: String,
    val ownerId: String,
    /** Blank only for the default group (then shown as "All workplaces"). */
    val name: String,
    val workplaceIds: List<String>,
    val isDefault: Boolean = false,
    /** SH-268: who manages every workplace of the group (saved by saveGroupManagers). */
    val managers: List<GroupManager> = emptyList()
) {
    /** The default group without a name of its own (the screens show "All workplaces"). */
    val usesDefaultName: Boolean get() = isDefault && name.isBlank()
}

/**
 * SH-268: a group manager: [userId] manages every workplace of the group at [permission]
 * ([UserPermission.MANAGER] or [UserPermission.MANAGER_FINANCIAL]), including workplaces added to
 * the group later. functions/groupManagers.js materialises it as an engagement in each of them
 * ([Engagement.groupGrant]).
 */
data class GroupManager(val userId: String, val permission: UserPermission)

/** SH-268: the group's managers cannot be saved: why (also the server's refusals). */
class InvalidGroupManagersException(val problem: GroupManagers.Problem) :
    Exception("Invalid group managers: $problem")

/** SH-268: the rules of a group's managers, the same as functions/groupManagers.js checks. */
object GroupManagers {
    const val MAX_MANAGERS = 10

    /** The levels a group gives. */
    val LEVELS: List<UserPermission> = listOf(UserPermission.MANAGER, UserPermission.MANAGER_FINANCIAL)

    enum class Problem {
        /** More than [MAX_MANAGERS], a level other than [LEVELS], or a person twice. */
        MALFORMED,
        /** The owner manages everything already. */
        OWNER_IS_NOT_A_MANAGER,
        /** Not (any more) working in one of the owner's workplaces. */
        NOT_IN_BUSINESS,
        /** Invited but not signed up yet. */
        NOT_SIGNED_UP,
        /** The group's workplaces would give them more than 6 engagements. */
        TOO_MANY_WORKPLACES,
        /** Only the group's owner names its managers. */
        NOT_YOUR_GROUP
    }

    /** What is wrong with [managers] for a group of [ownerId] (null: it can be saved). */
    fun problem(managers: List<GroupManager>, ownerId: String): Problem? = when {
        managers.size > MAX_MANAGERS -> Problem.MALFORMED
        managers.any { it.permission !in LEVELS } -> Problem.MALFORMED
        managers.map { it.userId }.toSet().size != managers.size -> Problem.MALFORMED
        managers.any { it.userId == ownerId } -> Problem.OWNER_IS_NOT_A_MANAGER
        else -> null
    }

    /**
     * Who may be named a manager of [group]: of the people working in its workplaces
     * ([teams]: workplace id to its active people), everyone but the owner and those already
     * managing it, each once, by name. People still waiting for their invitation are left out
     * (they could not sign in yet).
     */
    fun candidates(group: WorkplaceGroup, teams: Map<String, List<User>>): List<User> {
        val managing = group.managers.map { it.userId }.toSet()
        return group.workplaceIds.flatMap { teams[it].orEmpty() }
            .filter { !it.isShadow && it.uid != group.ownerId && it.uid !in managing }
            .distinctBy { it.uid }
            .sortedBy { it.displayName.lowercase() }
    }

    /** [group] with [userId] managing it at [permission] (added, or their level changed). */
    fun put(group: WorkplaceGroup, userId: String, permission: UserPermission): WorkplaceGroup {
        val others = group.managers.filterNot { it.userId == userId }
        val at = group.managers.indexOfFirst { it.userId == userId }
        val manager = GroupManager(userId, permission)
        return group.copy(managers = if (at < 0) others + manager else others.toMutableList().apply { add(at, manager) })
    }

    /** [group] without [userId] among its managers. */
    fun remove(group: WorkplaceGroup, userId: String): WorkplaceGroup =
        group.copy(managers = group.managers.filterNot { it.userId == userId })
}

/** SH-267: the owner's grouping cannot be saved: what is wrong ([WorkplaceGroups.problems]). */
class InvalidWorkplaceGroupsException(val problems: Set<WorkplaceGroups.Problem>) :
    Exception("Invalid workplace groups: $problems")

/** SH-267: the rules of a grouping, the same as functions/workplaceGroups.js checks. */
object WorkplaceGroups {
    /** The rules unroll per-workplace checks for up to 6 workplaces (firestore.rules). */
    const val MAX_WORKPLACES = 6

    /** Never more groups than an owner can have workplaces. */
    const val MAX_GROUPS = 6

    const val MAX_NAME = 60

    enum class Problem {
        /** A group other than the default one has no name. */
        NAME_MISSING,
        NAME_TOO_LONG,
        /** A workplace is listed in two groups. */
        WORKPLACE_IN_TWO_GROUPS,
        /** A group lists more than [MAX_WORKPLACES] workplaces, or there are more than [MAX_GROUPS] groups. */
        TOO_MANY,
        /** A group lists a workplace the owner does not own. */
        WORKPLACE_NOT_OWNED
    }

    /** What is wrong with [groups] for an owner of [owned] (empty: it can be saved). */
    fun problems(groups: List<WorkplaceGroup>, owned: Set<String>): Set<Problem> = buildSet {
        if (groups.size > MAX_GROUPS || groups.any { it.workplaceIds.size > MAX_WORKPLACES }) add(Problem.TOO_MANY)
        groups.forEach { group ->
            val name = group.name.trim()
            if (name.isEmpty() && !group.isDefault) add(Problem.NAME_MISSING)
            if (name.length > MAX_NAME) add(Problem.NAME_TOO_LONG)
            if (group.workplaceIds.any { it !in owned }) add(Problem.WORKPLACE_NOT_OWNED)
        }
        val listed = groups.flatMap { it.workplaceIds }
        if (listed.size != listed.toSet().size) add(Problem.WORKPLACE_IN_TWO_GROUPS)
    }

    /** [groups] with [workplaceId] moved into [toGroupId] (null: into no group). */
    fun move(groups: List<WorkplaceGroup>, workplaceId: String, toGroupId: String?): List<WorkplaceGroup> =
        groups.map { group ->
            val without = group.workplaceIds - workplaceId
            if (group.id == toGroupId) group.copy(workplaceIds = without + workplaceId) else group.copy(workplaceIds = without)
        }

    /** The group [workplaceId] is in, if any. */
    fun groupOf(groups: List<WorkplaceGroup>, workplaceId: String): WorkplaceGroup? =
        groups.firstOrNull { workplaceId in it.workplaceIds }

    /** Of [owned] (in their order), the workplaces in no group. */
    fun ungrouped(groups: List<WorkplaceGroup>, owned: List<String>): List<String> {
        val grouped = groups.flatMap { it.workplaceIds }.toSet()
        return owned.filter { it !in grouped }
    }

    /**
     * The other workplaces [workplaceId] may borrow people from: the rest of its group (none when
     * it is in no group).
     */
    fun lendersTo(groups: List<WorkplaceGroup>, workplaceId: String): List<String> =
        groupOf(groups, workplaceId)?.workplaceIds?.filter { it != workplaceId }.orEmpty()
}
